import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiChevronDown, FiPlus, FiTrash2, FiChevronUp, FiFileText, FiTruck, FiAlertCircle, FiClipboard, FiCopy, FiNavigation, FiUpload } from 'react-icons/fi';
import api from '../../services/api';
import ContainerTab from '../truck/ContainerTab';
import AccountingTab from '../truck/AccountingTab';
import DocCenterTab from '../truck/DocCenterTab';
import { getTrackingUrl } from '../../config/carrierTracking';
import StatusSelector from '../../components/common/StatusSelector';
import TradePartnerAutocomplete from '../../components/common/TradePartnerAutocomplete';
import ImportShipmentModal from '../../components/ImportShipmentModal';

const OceanImportDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'Basic');
  const [hbls, setHbls] = useState([]);
  const [hblTools, setHblTools] = useState(null);   // which house's Tools menu is open
  const [expandedHbls, setExpandedHbls] = useState({});
  const [showTools, setShowTools] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const initialLoadDone = useRef(false);
  const toolsRef = useRef(null);

  const emptyMbl = {
    file_no: '',
    mbl_no: '', office: '', bl_type: 'NORMAL', post_date: new Date().toISOString().split('T')[0],
    oversea_agent: '', carrier: '', bl_acct_carrier: '', forwarding_agent: '',
    co_loader: '', agent_ref_no: '', sub_bl_no: '', op: '',
    carrier_contract_no: '', direct_master: false,
    vessel: '', voyage: '', cy_location: '', cfs_location: '', freight_location: '',
    port_of_loading: '', etd: '', port_of_discharge: '', eta: '',
    place_of_delivery: '', place_of_delivery_eta: '', final_destination: '', final_eta: '',
    atd: '', ata: '', etb: '',
    freight: '', ship_mode: 'FCL', service_term_from: 'CY', service_term_to: 'CY',
    container_qty: '',
    obl_type: '', obl_received: false, released_date: '',
    business_referred_by: '', latest_gate_in: '',
    status: 'Pending', memo: '',
  };

  const [formData, setFormData] = useState(emptyMbl);
  const [shipmentId, setShipmentId] = useState(id || null);

  useEffect(() => {
    if (id) {
      loadShipment(id);
    } else {
      // New shipment mode - mark initial load done so changes are tracked
      setTimeout(() => { initialLoadDone.current = true; }, 0);
    }
  }, [id]);

  // Close tools dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setShowTools(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Track dirty state from any formData change (including TradePartnerAutocomplete)
  useEffect(() => {
    if (initialLoadDone.current) {
      setIsDirty(true);
    }
  }, [formData]);

  // Warn user about unsaved changes when leaving page
  useEffect(() => {
    if (!isNew && !isDirty) return;
    if (isNew || isDirty) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = '';
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [isNew, isDirty]);

  const loadShipment = async (sid) => {
    try {
      const res = await api.get(`/ocean-import/${sid}`);
      const data = res.data;
      const dateFields = ['post_date','etd','eta','place_of_delivery_eta','final_eta','atd','ata','etb','released_date'];
      const mapped = {};
      Object.keys(emptyMbl).forEach(k => {
        if (dateFields.includes(k)) {
          mapped[k] = data[k] ? data[k].split('T')[0] : '';
        } else if (typeof emptyMbl[k] === 'boolean') {
          mapped[k] = !!data[k];
        } else {
          mapped[k] = data[k] || '';
        }
      });
      setFormData(mapped);
      setShipmentId(data.id);
      if (data.hbls && data.hbls.length > 0) {
        setHbls(data.hbls);
        const expanded = {};
        data.hbls.forEach(h => { expanded[h.id] = true; });
        setExpandedHbls(expanded);
      }
    } catch (error) {
      toast.error('Failed to load shipment');
      navigate('/ocean-import');
    } finally {
      setLoading(false);
      setTimeout(() => { initialLoadDone.current = true; }, 0);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setIsDirty(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        // Create new shipment on first save
        const { file_no, ...createData } = formData;
        const res = await api.post('/ocean-import', createData);
        const data = res.data;
        setShipmentId(data.id);
        setFormData(prev => ({ ...prev, file_no: data.file_no }));
        setIsDirty(false);
        toast.success(`Shipment ${data.file_no} created`);
        navigate(`/ocean-import/${data.id}`, { replace: true });
      } else {
        const { file_no, ...updateData } = formData;
        await api.put(`/ocean-import/${shipmentId}`, updateData);
        setIsDirty(false);
        toast.success('Shipment saved');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ==================== HBL CRUD ====================
  const emptyHbl = {
    hbl_no: '', ams_no: '', isf_no: '', isf_filing_3rd_party: false,
    quotation_no: '', shipper: '', consignee: '', notify: '',
    customer: '', bill_to: '', sub_bl_no: '', op: '', sales: '',
    forwarding_agent: '', isf_matched_date: '',
    customs_broker: '', trucker: '', cy_cfs_location: '', available: '',
    place_of_delivery: '', place_of_delivery_eta: '', final_destination: '', final_eta: '',
    delivery_location: '', ship_mode: 'FCL', freight: '', lfd: '',
    rail: false, it_no: '', it_date: '', it_issued_location: '',
    go_date: '', expiry_date: '',
    express_bl: false, sales_type: '', incoterms: '', cargo_type: 'GENERAL CARGO',
    door_move: false, c_clearance: false, c_hold: false, c_released_date: '',
    service_term_from: 'CY', service_term_to: 'CY', container_qty: '',
    business_referred_by: '', entry_no: '', entry_doc_sent: false,
    obl_received: false, ror: false, hold: false, freight_released: false,
    released_by: '', door_delivered: '', an_sent_date: '', do_sent_date: '',
    package_count: '', weight_kg: '', measurement_cbm: '',
    status: 'Pending', memo: '',
  };

  const addHbl = async () => {
    try {
      const res = await api.post(`/ocean-import/${shipmentId}/hbl`, {});
      const newHbl = res.data;
      setHbls(prev => [...prev, newHbl]);
      setExpandedHbls(prev => ({ ...prev, [newHbl.id]: true }));
      toast.success('HB/L added');
    } catch (error) {
      toast.error('Failed to add HB/L');
    }
  };

  const handleHblChange = (hblId, e) => {
    const { name, value, type, checked } = e.target;
    setHbls(prev => prev.map(h =>
      h.id === hblId ? { ...h, [name]: type === 'checkbox' ? checked : value } : h
    ));
  };

  const setHblField = (hblId, name, value) => {
    setHbls(prev => prev.map(h =>
      h.id === hblId ? { ...h, [name]: value } : h
    ));
  };

  const saveHbl = async (hblId) => {
    const hbl = hbls.find(h => h.id === hblId);
    if (!hbl) return;
    try {
      const { id: _, shipment_id, created_at, updated_at, ...updateData } = hbl;
      await api.put(`/ocean-import/hbl/${hblId}`, updateData);
      toast.success('HB/L saved');
    } catch (error) {
      toast.error('Failed to save HB/L');
    }
  };

  const deleteHbl = async (hblId) => {
    if (!window.confirm('Delete this HB/L?')) return;
    try {
      await api.delete(`/ocean-import/hbl/${hblId}`);
      setHbls(prev => prev.filter(h => h.id !== hblId));
      toast.success('HB/L deleted');
    } catch (error) {
      toast.error('Failed to delete HB/L');
    }
  };

  const toggleHbl = (hblId) => {
    setExpandedHbls(prev => ({ ...prev, [hblId]: !prev[hblId] }));
  };

  const formatDate = (d) => d ? d.split('T')[0] : '';

  if (loading) {
    return <div className="loading"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="shipment-page">
      {/* Breadcrumb */}
      <div className="shipment-breadcrumb">
        <button className="breadcrumb-back-btn" onClick={() => {
          if ((isNew || isDirty) && !window.confirm('You are leaving the page without saving. Are you sure?')) return;
          navigate('/ocean-import');
        }} title="Back">
          <FiArrowLeft />
        </button>
        <span>Home</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span>Ocean Import</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span className="breadcrumb-current">{formData.file_no || 'New'}</span>
      </div>

      {/* Tab Bar */}
      <div className="shipment-tabs">
        {['Basic', 'Container', 'Accounting', 'Doc Center'].map(tab => (
          <button
            key={tab}
            className={`shipment-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Header Summary Bar */}
      <div className="shipment-header-bar">
        <div className="header-left">
          <span className="header-mbl">
            <span className="mbl-label">MB/L</span>
            <span className="mbl-value">{formData.mbl_no || '-'}</span>
          </span>
          <span className="header-customer">
            <span className="customer-label">Oversea Agent</span>
            <span className="customer-value">{formData.oversea_agent || '-'}</span>
          </span>
        </div>
        <div className="header-right">
          <StatusSelector
            value={formData.status}
            options={['Pending', 'In Transit', 'Arrived', 'Delivered', 'Closed']}
            onChange={(val) => { setFormData(prev => ({ ...prev, status: val })); setIsDirty(true); }}
          />
          <span className="header-route">
            {formData.port_of_loading || '-'} ({formData.etd || '-'}) &gt; {formData.port_of_discharge || '-'} ({formData.eta || '-'})
          </span>
          {formData.mbl_no && (
            <button
              className="header-tools-btn"
              style={{ background: '#2563eb', color: '#fff', marginRight: '8px' }}
              onClick={() => {
                const tracking = getTrackingUrl(formData.vessel_flight_no || '', formData.mbl_no, 'ocean-import');
                if (tracking) window.open(tracking.url, '_blank');
              }}
              title="Track shipment online"
            >
              <FiNavigation size={14} style={{ marginRight: '4px' }} /> Track
            </button>
          )}
          <div ref={toolsRef} style={{ position: 'relative' }}>
            <button className="header-tools-btn" onClick={() => setShowTools(!showTools)}>
              Tools <FiChevronDown />
            </button>
            {showTools && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, zIndex: 200,
                background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '220px',
                marginTop: '4px', overflow: 'hidden',
              }}>
                <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>Documents</div>
                <button onClick={() => { setShowTools(false); window.open(`/documents/ocean-import/${shipmentId}/delivery-order`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiTruck size={15} style={{ color: '#2563eb' }} /> Pickup / Delivery Order
                </button>
                <button onClick={() => { setShowTools(false); window.open(`/documents/ocean-import/${shipmentId}/bill-of-lading`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiClipboard size={15} style={{ color: '#059669' }} /> Bill of Lading
                </button>
                <button onClick={() => { setShowTools(false); window.open(`/documents/ocean-import/${shipmentId}/arrival-notice`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiAlertCircle size={15} style={{ color: '#d97706' }} /> Arrival Notice
                </button>
                <div style={{ borderTop: '1px solid #f1f5f9', margin: '2px 0' }}></div>
                <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Actions</div>
                <button onClick={() => { setShowTools(false); setShowUploadModal(true); }} style={toolsMenuItemStyle}>
                  <FiUpload size={15} style={{ color: '#2563eb' }} /> Upload Document (Update)
                </button>
                <button onClick={async () => {
                  setShowTools(false);
                  if (!window.confirm('Copy this shipment? A new shipment will be created with same customer, delivery info, and charges (MBL/HBL/Container left blank).')) return;
                  try {
                    const res = await api.post(`/ocean-import/${shipmentId}/copy`);
                    toast.success(`Shipment copied! New File No: ${res.data.file_no}`);
                    navigate(`/ocean-import/${res.data.id}`);
                  } catch (err) {
                    toast.error('Failed to copy shipment');
                  }
                }} style={toolsMenuItemStyle}>
                  <FiCopy size={15} style={{ color: '#6366f1' }} /> Copy Shipment
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==================== BASIC TAB ==================== */}
      {activeTab === 'Basic' && (
        <div className="oi-basic-content">
          <form onSubmit={handleSave} className="shipment-form">

            {/* MBL Section */}
            <div className="oi-mbl-section">
              <div className="form-section-header">MBL Information</div>
              <div className="shipment-form-grid">
                <div className="shipment-form-group">
                  <label>File No.</label>
                  <input type="text" value={formData.file_no} disabled />
                </div>
                <div className="shipment-form-group">
                  <label className="required">MB/L No.</label>
                  <input type="text" name="mbl_no" value={formData.mbl_no} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>B/L Type</label>
                  <select name="bl_type" value={formData.bl_type} onChange={handleChange}>
                    <option value="NORMAL">NORMAL</option>
                    <option value="SEAWAY BILL">SEAWAY BILL</option>
                    <option value="EXPRESS">EXPRESS</option>
                  </select>
                </div>
                <div className="shipment-form-group">
                  <label>Post Date</label>
                  <input type="date" name="post_date" value={formData.post_date} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Oversea Agent</label>
                  <TradePartnerAutocomplete value={formData.oversea_agent} tpTypes="Customer,Vendor" placeholder="Search oversea agent..."
                    onChange={(val) => setFormData(prev => ({ ...prev, oversea_agent: val }))} />
                </div>
                <div className="shipment-form-group">
                  <label>Carrier</label>
                  <input type="text" name="carrier" value={formData.carrier} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>B/L Acct. Carrier</label>
                  <input type="text" name="bl_acct_carrier" value={formData.bl_acct_carrier} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Forwarding Agent</label>
                  <TradePartnerAutocomplete value={formData.forwarding_agent} tpTypes="Customer,Vendor" placeholder="Search forwarding agent..."
                    onChange={(val) => setFormData(prev => ({ ...prev, forwarding_agent: val }))} />
                </div>
                <div className="shipment-form-group">
                  <label>Co-loader</label>
                  <input type="text" name="co_loader" value={formData.co_loader} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Agent Ref No.</label>
                  <input type="text" name="agent_ref_no" value={formData.agent_ref_no} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Sub B/L No.</label>
                  <input type="text" name="sub_bl_no" value={formData.sub_bl_no} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>OP</label>
                  <input type="text" name="op" value={formData.op} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Carrier Contract No.</label>
                  <input type="text" name="carrier_contract_no" value={formData.carrier_contract_no} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Direct Master</label>
                  <div style={{ paddingTop: '6px' }}>
                    <input type="checkbox" name="direct_master" checked={formData.direct_master} onChange={handleChange} />
                  </div>
                </div>
              </div>

              {/* Vessel & Voyage */}
              <div className="form-section-header">Vessel & Voyage</div>
              <div className="shipment-form-grid">
                <div className="shipment-form-group">
                  <label>Vessel</label>
                  <input type="text" name="vessel" value={formData.vessel} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Voyage</label>
                  <input type="text" name="voyage" value={formData.voyage} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>CY Location</label>
                  <input type="text" name="cy_location" value={formData.cy_location} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>CFS Location</label>
                  <input type="text" name="cfs_location" value={formData.cfs_location} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Freight Location</label>
                  <TradePartnerAutocomplete value={formData.freight_location} placeholder="Freight location..."
                    onChange={(val) => setFormData(prev => ({ ...prev, freight_location: val }))} />
                </div>
              </div>

              {/* Route */}
              <div className="form-section-header">Route</div>
              <div className="shipment-form-grid">
                <div className="shipment-form-group">
                  <label>Port of Loading</label>
                  <TradePartnerAutocomplete value={formData.port_of_loading} placeholder="Port of loading..."
                    onChange={(val) => setFormData(prev => ({ ...prev, port_of_loading: val }))} />
                </div>
                <div className="shipment-form-group">
                  <label>ETD</label>
                  <input type="date" name="etd" value={formData.etd} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Port of Discharge</label>
                  <TradePartnerAutocomplete value={formData.port_of_discharge} placeholder="Port of discharge..."
                    onChange={(val) => setFormData(prev => ({ ...prev, port_of_discharge: val }))} />
                </div>
                <div className="shipment-form-group">
                  <label className="required">ETA</label>
                  <input type="date" name="eta" value={formData.eta} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Place of Delivery</label>
                  <TradePartnerAutocomplete value={formData.place_of_delivery} placeholder="Place of delivery..."
                    onChange={(val) => setFormData(prev => ({ ...prev, place_of_delivery: val }))} />
                </div>
                <div className="shipment-form-group">
                  <label>Place of Delivery ETA</label>
                  <input type="date" name="place_of_delivery_eta" value={formData.place_of_delivery_eta} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Final Destination</label>
                  <TradePartnerAutocomplete value={formData.final_destination} placeholder="Final destination..."
                    onChange={(val) => setFormData(prev => ({ ...prev, final_destination: val }))} />
                </div>
                <div className="shipment-form-group">
                  <label>Final ETA</label>
                  <input type="date" name="final_eta" value={formData.final_eta} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>ATD</label>
                  <input type="date" name="atd" value={formData.atd} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>ATA</label>
                  <input type="date" name="ata" value={formData.ata} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>ETB</label>
                  <input type="date" name="etb" value={formData.etb} onChange={handleChange} />
                </div>
              </div>

              {/* Freight & Shipping */}
              <div className="form-section-header">Freight & Shipping</div>
              <div className="shipment-form-grid">
                <div className="shipment-form-group">
                  <label>Freight</label>
                  <select name="freight" value={formData.freight} onChange={handleChange}>
                    <option value="">--</option>
                    <option value="PREPAID">PREPAID</option>
                    <option value="COLLECT">COLLECT</option>
                  </select>
                </div>
                <div className="shipment-form-group">
                  <label>Ship Mode</label>
                  <select name="ship_mode" value={formData.ship_mode} onChange={handleChange}>
                    <option value="FCL">FCL</option>
                    <option value="LCL">LCL</option>
                  </select>
                </div>
                <div className="shipment-form-group">
                  <label>Service Term</label>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <select name="service_term_from" value={formData.service_term_from} onChange={handleChange} style={{ flex: 1 }}>
                      <option value="CY">CY</option>
                      <option value="CFS">CFS</option>
                      <option value="DOOR">DOOR</option>
                    </select>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>to</span>
                    <select name="service_term_to" value={formData.service_term_to} onChange={handleChange} style={{ flex: 1 }}>
                      <option value="CY">CY</option>
                      <option value="CFS">CFS</option>
                      <option value="DOOR">DOOR</option>
                    </select>
                  </div>
                </div>
                <div className="shipment-form-group">
                  <label>Container/Qty</label>
                  <input type="text" name="container_qty" value={formData.container_qty} onChange={handleChange} placeholder="e.g. 1x40HQ" />
                </div>
              </div>

              {/* OBL & Status */}
              <div className="form-section-header">OB/L & Status</div>
              <div className="shipment-form-grid">
                <div className="shipment-form-group">
                  <label>OB/L Type</label>
                  <select name="obl_type" value={formData.obl_type} onChange={handleChange}>
                    <option value="">--</option>
                    <option value="ORIGINAL">ORIGINAL</option>
                    <option value="TELEX RELEASE">TELEX RELEASE</option>
                    <option value="SEAWAY BILL">SEAWAY BILL</option>
                    <option value="EXPRESS">EXPRESS</option>
                  </select>
                </div>
                <div className="shipment-form-group">
                  <label>OB/L Received</label>
                  <div style={{ paddingTop: '6px' }}>
                    <input type="checkbox" name="obl_received" checked={formData.obl_received} onChange={handleChange} />
                  </div>
                </div>
                <div className="shipment-form-group">
                  <label>Released Date</label>
                  <input type="date" name="released_date" value={formData.released_date} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Business Referred By</label>
                  <input type="text" name="business_referred_by" value={formData.business_referred_by} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Latest Gate In</label>
                  <input type="text" name="latest_gate_in" value={formData.latest_gate_in} onChange={handleChange} />
                </div>
                <div className="shipment-form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="Pending">Pending</option>
                    <option value="In Transit">In Transit</option>
                    <option value="Arrived">Arrived</option>
                    <option value="Delivered">Delivered</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
              </div>

              {/* Memo */}
              <div className="form-section-header">Memo</div>
              <div className="shipment-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="shipment-form-group">
                  <textarea
                    name="memo"
                    value={formData.memo}
                    onChange={handleChange}
                    rows={3}
                    style={{
                      width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0',
                      borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit',
                      resize: 'vertical', color: '#1e293b',
                    }}
                  />
                </div>
              </div>

              <div className="shipment-form-actions">
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : 'Save MBL'}
                </button>
              </div>
            </div>

            {/* ==================== HBL Section ==================== */}
            <div className="oi-hbl-section">
              <div className="oi-hbl-header">
                <span className="oi-hbl-title">House B/L ({hbls.length})</span>
                <button type="button" className="btn-add-hbl" onClick={addHbl} disabled={isNew} title={isNew ? 'Save shipment first' : ''}>
                  <FiPlus size={13} /> Add HB/L
                </button>
              </div>

              {hbls.length === 0 && (
                <div className="oi-hbl-empty">
                  No House B/L added yet. Click "+ Add HB/L" to create one.
                </div>
              )}

              {hbls.map((hbl) => (
                <div className="oi-hbl-card" key={hbl.id}>
                  {/* HBL Header (yellow) */}
                  <div className="oi-hbl-card-header" onClick={() => toggleHbl(hbl.id)}>
                    <div className="oi-hbl-card-info">
                      <span className="oi-hbl-no">{hbl.hbl_no || '(No HB/L No.)'}</span>
                      <span className="oi-hbl-customer">{hbl.customer || ''}</span>
                      {hbl.op && <span className="oi-hbl-op">OP: {hbl.op}</span>}
                      <span className={`status-badge status-${(hbl.status || 'pending').toLowerCase()}`}>
                        {hbl.status || 'Pending'}
                      </span>
                    </div>
                    <div className="oi-hbl-card-actions">
                      {/* This house bill's own documents. Opening them from
                          here is what makes them THIS house's: the hbl_id
                          rides along, so the document renders for one house
                          and the operator never picks from a list — which is
                          also what stops one house's letterhead going out over
                          another house's cargo. */}
                      <div style={{ position: 'relative' }}
                           onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="header-tools-btn"
                          style={{ padding: '3px 8px', fontSize: 12 }}
                          onClick={() => setHblTools(hblTools === hbl.id ? null : hbl.id)}
                          title="Documents for this house bill"
                        >
                          Tools <FiChevronDown size={12} />
                        </button>
                        {hblTools === hbl.id && (
                          <div style={{
                            position: 'absolute', top: '100%', right: 0, zIndex: 300,
                            background: '#fff', border: '1px solid #d1d5db',
                            borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.15)',
                            minWidth: 230, marginTop: 4, overflow: 'hidden',
                          }}>
                            <div style={{
                              padding: '6px 12px', fontSize: 11, fontWeight: 600,
                              color: '#94a3b8', textTransform: 'uppercase',
                              borderBottom: '1px solid #f1f5f9',
                            }}>
                              {hbl.hbl_no || 'This house bill'}
                            </div>
                            {!hbl.id || String(hbl.id).startsWith('new') ? (
                              <div style={{ padding: '10px 12px', fontSize: 12, color: '#b45309' }}>
                                Save the house bill first.
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setHblTools(null);
                                    window.open(`/documents/ocean-import/${shipmentId}/arrival-notice?hbl_id=${hbl.id}`, '_blank');
                                  }}
                                  style={toolsMenuItemStyle}
                                >
                                  <FiAlertCircle size={15} style={{ color: '#d97706' }} /> Arrival Notice
                                </button>
                                <button
                                  onClick={() => {
                                    setHblTools(null);
                                    window.open(`/documents/ocean-import/${shipmentId}/delivery-order?hbl_id=${hbl.id}`, '_blank');
                                  }}
                                  style={toolsMenuItemStyle}
                                >
                                  <FiTruck size={15} style={{ color: '#2563eb' }} /> Delivery Order
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <button type="button" className="btn-delete-hbl" onClick={(e) => { e.stopPropagation(); deleteHbl(hbl.id); }} title="Delete HB/L">
                        <FiTrash2 size={13} />
                      </button>
                      {expandedHbls[hbl.id] ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                    </div>
                  </div>

                  {/* HBL Body (collapsible) */}
                  {expandedHbls[hbl.id] && (
                    <div className="oi-hbl-card-body">
                      <div className="form-section-header">General</div>
                      <div className="shipment-form-grid">
                        <FG label="HB/L No." name="hbl_no" value={hbl.hbl_no} onChange={(e) => handleHblChange(hbl.id, e)} required />
                        <FG label="AMS No." name="ams_no" value={hbl.ams_no} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FG label="ISF No." name="isf_no" value={hbl.isf_no} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGCheck label="ISF Filing by 3rd Party" name="isf_filing_3rd_party" checked={!!hbl.isf_filing_3rd_party} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FG label="Quotation No." name="quotation_no" value={hbl.quotation_no} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <div className="shipment-form-group"><label>Shipper</label>
                          <TradePartnerAutocomplete value={hbl.shipper} tpTypes="Customer,Vendor" placeholder="Search shipper..."
                            onChange={(val) => setHblField(hbl.id, 'shipper', val)} /></div>
                        <div className="shipment-form-group"><label>Consignee</label>
                          <TradePartnerAutocomplete value={hbl.consignee} tpTypes="Customer,Vendor" placeholder="Search consignee..."
                            onChange={(val) => setHblField(hbl.id, 'consignee', val)} /></div>
                        <div className="shipment-form-group"><label>Notify</label>
                          <TradePartnerAutocomplete value={hbl.notify} tpTypes="Customer,Vendor" placeholder="Search notify..."
                            onChange={(val) => setHblField(hbl.id, 'notify', val)} /></div>
                        <div className="shipment-form-group"><label>Customer</label>
                          <TradePartnerAutocomplete value={hbl.customer} placeholder="Search customer..."
                            onChange={(val) => setHblField(hbl.id, 'customer', val)} /></div>
                        <div className="shipment-form-group"><label>Bill To</label>
                          <TradePartnerAutocomplete value={hbl.bill_to} placeholder="Search bill to..."
                            onChange={(val) => setHblField(hbl.id, 'bill_to', val)} /></div>
                        <FG label="Sub B/L No." name="sub_bl_no" value={hbl.sub_bl_no} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FG label="OP" name="op" value={hbl.op} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FG label="Sales" name="sales" value={hbl.sales} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <div className="shipment-form-group"><label>Forwarding Agent</label>
                          <TradePartnerAutocomplete value={hbl.forwarding_agent} tpTypes="Customer,Vendor" placeholder="Search forwarding agent..."
                            onChange={(val) => setHblField(hbl.id, 'forwarding_agent', val)} /></div>
                        <FGDate label="ISF Matched Date" name="isf_matched_date" value={formatDate(hbl.isf_matched_date)} onChange={(e) => handleHblChange(hbl.id, e)} />
                      </div>

                      <div className="form-section-header">Delivery</div>
                      <div className="shipment-form-grid">
                        <div className="shipment-form-group"><label>Customs Broker</label>
                          <TradePartnerAutocomplete value={hbl.customs_broker} tpTypes="Customer,Vendor" placeholder="Search customs broker..."
                            onChange={(val) => setHblField(hbl.id, 'customs_broker', val)} /></div>
                        <div className="shipment-form-group"><label>Trucker</label>
                          <TradePartnerAutocomplete value={hbl.trucker} tpTypes="Trucker" placeholder="Search trucker..."
                            onChange={(val) => setHblField(hbl.id, 'trucker', val)} /></div>
                        <FG label="CY/CFS Location" name="cy_cfs_location" value={hbl.cy_cfs_location} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGDate label="Available" name="available" value={formatDate(hbl.available)} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <div className="shipment-form-group"><label>Place of Delivery</label>
                          <TradePartnerAutocomplete value={hbl.place_of_delivery} placeholder="Place of delivery..."
                            onChange={(val) => setHblField(hbl.id, 'place_of_delivery', val)} /></div>
                        <FGDate label="Place of Delivery ETA" name="place_of_delivery_eta" value={formatDate(hbl.place_of_delivery_eta)} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <div className="shipment-form-group"><label>Final Destination</label>
                          <TradePartnerAutocomplete value={hbl.final_destination} placeholder="Final destination..."
                            onChange={(val) => setHblField(hbl.id, 'final_destination', val)} /></div>
                        <FGDate label="Final ETA" name="final_eta" value={formatDate(hbl.final_eta)} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FG label="Delivery Location" name="delivery_location" value={hbl.delivery_location} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGSelect label="Ship Mode" name="ship_mode" value={hbl.ship_mode || 'FCL'} onChange={(e) => handleHblChange(hbl.id, e)} options={[['FCL','FCL'],['LCL','LCL']]} />
                        <FGSelect label="Freight" name="freight" value={hbl.freight} onChange={(e) => handleHblChange(hbl.id, e)} options={[['','--'],['PREPAID','PREPAID'],['COLLECT','COLLECT']]} />
                        <FGDate label="LFD" name="lfd" value={formatDate(hbl.lfd)} onChange={(e) => handleHblChange(hbl.id, e)} />
                      </div>

                      <div className="form-section-header">IT Information</div>
                      <div className="shipment-form-grid">
                        <FGCheck label="Rail" name="rail" checked={!!hbl.rail} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FG label="IT No." name="it_no" value={hbl.it_no} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGDate label="IT Date" name="it_date" value={formatDate(hbl.it_date)} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FG label="IT Issued Location" name="it_issued_location" value={hbl.it_issued_location} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGDate label="G.O Date" name="go_date" value={formatDate(hbl.go_date)} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGDate label="Expiry Date" name="expiry_date" value={formatDate(hbl.expiry_date)} onChange={(e) => handleHblChange(hbl.id, e)} />
                      </div>

                      <div className="form-section-header">Sales & Terms</div>
                      <div className="shipment-form-grid">
                        <FGCheck label="Express B/L" name="express_bl" checked={!!hbl.express_bl} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FG label="Sales Type" name="sales_type" value={hbl.sales_type} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FG label="Incoterms" name="incoterms" value={hbl.incoterms} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGSelect label="Cargo Type" name="cargo_type" value={hbl.cargo_type || 'GENERAL CARGO'} onChange={(e) => handleHblChange(hbl.id, e)} options={[['GENERAL CARGO','GENERAL CARGO'],['HAZ','HAZ'],['REEFER','REEFER']]} />
                        <FGCheck label="Door Move" name="door_move" checked={!!hbl.door_move} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGCheck label="C.Clearance" name="c_clearance" checked={!!hbl.c_clearance} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGCheck label="C.Hold" name="c_hold" checked={!!hbl.c_hold} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGDate label="C. Released Date" name="c_released_date" value={formatDate(hbl.c_released_date)} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <div className="shipment-form-group">
                          <label>Service Term</label>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <select name="service_term_from" value={hbl.service_term_from || 'CY'} onChange={(e) => handleHblChange(hbl.id, e)} style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', height: '34px' }}>
                              <option value="CY">CY</option>
                              <option value="CFS">CFS</option>
                              <option value="DOOR">DOOR</option>
                            </select>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>to</span>
                            <select name="service_term_to" value={hbl.service_term_to || 'CY'} onChange={(e) => handleHblChange(hbl.id, e)} style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', height: '34px' }}>
                              <option value="CY">CY</option>
                              <option value="CFS">CFS</option>
                              <option value="DOOR">DOOR</option>
                            </select>
                          </div>
                        </div>
                        <FG label="Container/Qty" name="container_qty" value={hbl.container_qty} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FG label="Business Referred By" name="business_referred_by" value={hbl.business_referred_by} onChange={(e) => handleHblChange(hbl.id, e)} />
                      </div>

                      <div className="form-section-header">Customs & Release</div>
                      <div className="shipment-form-grid">
                        <FG label="Entry No." name="entry_no" value={hbl.entry_no} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGCheck label="Entry DOC. Sent" name="entry_doc_sent" checked={!!hbl.entry_doc_sent} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGCheck label="OB/L Received" name="obl_received" checked={!!hbl.obl_received} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGCheck label="ROR" name="ror" checked={!!hbl.ror} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGCheck label="Hold" name="hold" checked={!!hbl.hold} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGCheck label="Freight Released" name="freight_released" checked={!!hbl.freight_released} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FG label="Released By" name="released_by" value={hbl.released_by} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGDate label="Door Delivered" name="door_delivered" value={formatDate(hbl.door_delivered)} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGDate label="AN Sent Date" name="an_sent_date" value={formatDate(hbl.an_sent_date)} onChange={(e) => handleHblChange(hbl.id, e)} />
                        <FGDate label="DO Sent Date" name="do_sent_date" value={formatDate(hbl.do_sent_date)} onChange={(e) => handleHblChange(hbl.id, e)} />
                      </div>

                      <div className="form-section-header">Cargo</div>
                      <div className="shipment-form-grid">
                        <FG label="Package Count" name="package_count" value={hbl.package_count} onChange={(e) => handleHblChange(hbl.id, e)} type="number" />
                        <FG label="Weight (KG)" name="weight_kg" value={hbl.weight_kg} onChange={(e) => handleHblChange(hbl.id, e)} type="number" />
                        <FG label="Measurement (CBM)" name="measurement_cbm" value={hbl.measurement_cbm} onChange={(e) => handleHblChange(hbl.id, e)} type="number" />
                      </div>

                      <div className="form-section-header">Memo</div>
                      <div className="shipment-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                        <div className="shipment-form-group">
                          <textarea
                            name="memo"
                            value={hbl.memo || ''}
                            onChange={(e) => handleHblChange(hbl.id, e)}
                            rows={2}
                            style={{
                              width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0',
                              borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit',
                              resize: 'vertical', color: '#1e293b',
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 0' }}>
                        <button type="button" className="btn-save" onClick={() => saveHbl(hbl.id)}>
                          Save HB/L
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </form>
        </div>
      )}

      {/* ==================== Container TAB ==================== */}
      {activeTab === 'Container' && shipmentId && (
        <ContainerTab shipmentId={shipmentId} shipmentType="ocean-import" />
      )}
      {activeTab === 'Container' && !shipmentId && (
        <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the shipment first to manage containers.</div>
      )}

      {/* ==================== Accounting TAB ==================== */}
      {activeTab === 'Accounting' && shipmentId && (
        <AccountingTab shipmentId={shipmentId} shipmentType="ocean_import" />
      )}
      {activeTab === 'Accounting' && !shipmentId && (
        <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the shipment first to manage accounting.</div>
      )}

      {/* ==================== Doc Center TAB ==================== */}
      {activeTab === 'Doc Center' && shipmentId && (
        <DocCenterTab shipmentId={shipmentId} shipmentType="ocean-import" />
      )}
      {activeTab === 'Doc Center' && !shipmentId && (
        <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the shipment first to access documents.</div>
      )}
      {/* Upload Document Modal for updating existing shipment */}
      {showUploadModal && shipmentId && (
        <ImportShipmentModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          defaultType="ocean-import"
          existingShipment={{ id: shipmentId, type: 'ocean-import' }}
          onUpdated={(updatedShipment) => {
            // Reload shipment data after update
            loadShipment(shipmentId);
          }}
        />
      )}
    </div>
  );
};

// ==================== Helper Components ====================
const FG = ({ label, name, value, onChange, required, type = 'text' }) => (
  <div className="shipment-form-group">
    <label className={required ? 'required' : ''}>{label}</label>
    <input type={type} name={name} value={value || ''} onChange={onChange} />
  </div>
);

const FGDate = ({ label, name, value, onChange }) => (
  <div className="shipment-form-group">
    <label>{label}</label>
    <input type="date" name={name} value={value || ''} onChange={onChange} />
  </div>
);

const FGCheck = ({ label, name, checked, onChange }) => (
  <div className="shipment-form-group">
    <label>{label}</label>
    <div style={{ paddingTop: '6px' }}>
      <input type="checkbox" name={name} checked={checked} onChange={onChange} />
    </div>
  </div>
);

const FGSelect = ({ label, name, value, onChange, options }) => (
  <div className="shipment-form-group">
    <label>{label}</label>
    <select name={name} value={value || ''} onChange={onChange}>
      {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  </div>
);

const toolsMenuItemStyle = {
  display: 'flex', alignItems: 'center', gap: '10px',
  width: '100%', padding: '10px 14px', border: 'none',
  background: 'transparent', cursor: 'pointer', fontSize: '13px',
  fontWeight: 500, color: '#334155', textAlign: 'left',
  transition: 'background 0.15s',
};

export default OceanImportDetail;
